import React from 'react';
import { Route, Navigate, Routes } from 'react-router-dom';
import lazyWithRetry from '../utils/lazyWithRetry';

// Public Pages
const Home = lazyWithRetry(() => import("../pages/Home"));
const MainPricingPage = lazyWithRetry(() => import("../pages/PricingPage"));
const SolutionsIndex = lazyWithRetry(() => import("../pages/SolutionsIndex"));
const IBCommerceSolution = lazyWithRetry(() => import("../pages/IBCommerceSolution"));
const IBCommerceOverview = lazyWithRetry(() => import("../pages/commerce-solutions/IBCommerceOverview"));
const IBWorkforceOverview = lazyWithRetry(() => import("../pages/workforce-solutions/IBWorkforceOverview"));
const IBWorkforceEmployees = lazyWithRetry(() => import("../pages/workforce-solutions/EmployeesPage"));
const IBWorkforcePayroll = lazyWithRetry(() => import("../pages/workforce-solutions/PayrollPage"));
const IBWorkforceBenefits = lazyWithRetry(() => import("../pages/workforce-solutions/BenefitsPage"));
const IBWorkforcePerformance = lazyWithRetry(() => import("../pages/workforce-solutions/PerformancePage"));
const IBWorkforceLearning = lazyWithRetry(() => import("../pages/workforce-solutions/LearningPage"));
const IBCapitalOverview = lazyWithRetry(() => import("../pages/capital-solutions/IBCapitalOverview"));
const IBCapitalBanks = lazyWithRetry(() => import("../pages/capital-solutions/BanksPage"));
const IBCapitalTreasury = lazyWithRetry(() => import("../pages/capital-solutions/TreasuryPage"));
const IBCapitalCredit = lazyWithRetry(() => import("../pages/capital-solutions/CreditPage"));
const IBCapitalForecasting = lazyWithRetry(() => import("../pages/capital-solutions/ForecastingPage"));
const IBCapitalOptimization = lazyWithRetry(() => import("../pages/capital-solutions/OptimizationPage"));
const IBCapitalPage = lazyWithRetry(() => import("../pages/capital-solutions/IBCapitalPage"));
const IBOperationsOverview = lazyWithRetry(() => import("../pages/operations-solutions/IBOperationsOverview"));
const IBOperationsProjects = lazyWithRetry(() => import("../pages/operations-solutions/ProjectsPage"));
const IBOperationsAssets = lazyWithRetry(() => import("../pages/operations-solutions/AssetsPage"));
const IBOperationsInventory = lazyWithRetry(() => import("../pages/operations-solutions/InventoryPage"));
const IBOperationsProduction = lazyWithRetry(() => import("../pages/operations-solutions/ProductionPage"));
const IBFinanceOverview = lazyWithRetry(() => import("../pages/finance-solutions/IBFinanceOverview"));
const IBFinanceAccounting = lazyWithRetry(() => import("../pages/finance-solutions/AccountingPage"));
const IBFinanceReporting = lazyWithRetry(() => import("../pages/finance-solutions/ReportingPage"));
const IBFinanceBudgeting = lazyWithRetry(() => import("../pages/finance-solutions/BudgetingPage"));
const IBFinanceReconciliation = lazyWithRetry(() => import("../pages/finance-solutions/ReconciliationPage"));
const IBFinanceCompliance = lazyWithRetry(() => import("../pages/finance-solutions/CompliancePage"));
const WorkspaceOverview = lazyWithRetry(() => import("../pages/WorkspaceOverview"));
const IntelligenceOverview = lazyWithRetry(() => import("../pages/IntelligenceOverview"));
const InsightsIndex = lazyWithRetry(() => import("../pages/InsightsIndex"));
const IntelligencePage = lazyWithRetry(() => import("../pages/IntelligencePage"));
const AboutPage = lazyWithRetry(() => import("../pages/AboutPage"));
const ContactPage = lazyWithRetry(() => import("../pages/ContactPage"));
const ResourcesPage = lazyWithRetry(() => import("../pages/ResourcesPage"));
const ContractPublicView = lazyWithRetry(() => import("../pages/commerce/revenue-workflow/ContractPublicView"));
const ProposalResponse = lazyWithRetry(() => import("../pages/commerce/revenue-workflow/ProposalResponse"));
const ContractPortal = lazyWithRetry(() => import("../pages/commerce/revenue-workflow/ContractPortal"));

// Public Recruitment & Careers Pages
const PublicCareersPage = lazyWithRetry(() => import("../pages/workforce/pages/PublicCareersPage"));
const PublicJobDetailPage = lazyWithRetry(() => import("../pages/workforce/pages/PublicJobDetailPage"));
const CandidateOfferReviewPage = lazyWithRetry(() => import("../pages/workforce/pages/CandidateOfferReviewPage"));
const PublicCandidateOnboardingPage = lazyWithRetry(() => import("../pages/workforce/pages/PublicCandidateOnboardingPage"));

// Legal Policy Pages
const PrivacyPolicy = lazyWithRetry(() => import("../pages/legal/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("../pages/legal/TermsOfService"));
const CookiePolicy = lazyWithRetry(() => import("../pages/legal/CookiePolicy"));

// Commerce Solution Sub-modules
const PartiesModule = lazyWithRetry(() => import("../pages/commerce-solutions/PartiesModule"));
const CustomersPage = lazyWithRetry(() => import("../pages/commerce-solutions/parties/CustomersPage"));
const VendorsPage = lazyWithRetry(() => import("../pages/commerce-solutions/parties/VendorsPage"));
const PartnersPage = lazyWithRetry(() => import("../pages/commerce-solutions/parties/PartnersPage"));
const ChannelsPage = lazyWithRetry(() => import("../pages/commerce-solutions/parties/ChannelsPage"));
const ProfilesPage = lazyWithRetry(() => import("../pages/commerce-solutions/parties/ProfilesPage"));
const CatalogModule = lazyWithRetry(() => import("../pages/commerce-solutions/CatalogModule"));
const ItemsPage = lazyWithRetry(() => import("../pages/commerce-solutions/catalog/ItemsPage"));
const PricingPage = lazyWithRetry(() => import("../pages/commerce-solutions/catalog/PricingPage"));
const CostingPage = lazyWithRetry(() => import("../pages/commerce-solutions/catalog/CostingPage"));
const PackagesPage = lazyWithRetry(() => import("../pages/commerce-solutions/catalog/PackagesPage"));
const RulesPage = lazyWithRetry(() => import("../pages/commerce-solutions/catalog/RulesPage"));
const RevenueModule = lazyWithRetry(() => import("../pages/commerce-solutions/RevenueModule"));
const GovernanceModule = lazyWithRetry(() => import("../pages/commerce-solutions/GovernanceModule"));
const PoliciesPage = lazyWithRetry(() => import("../pages/commerce-solutions/governance/PoliciesPage"));
const AuthorityPage = lazyWithRetry(() => import("../pages/commerce-solutions/governance/AuthorityPage"));
const LimitsPage = lazyWithRetry(() => import("../pages/commerce-solutions/governance/LimitsPage"));
const RiskPage = lazyWithRetry(() => import("../pages/commerce-solutions/governance/RiskPage"));
const AuditPage = lazyWithRetry(() => import("../pages/commerce-solutions/governance/AuditPage"));

import SolutionGuard from "./SolutionGuard";

const PublicRoutes = () => (
    <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/pricing" element={<MainPricingPage />} />
        <Route path="/workspace-overview" element={<WorkspaceOverview />} />
        <Route path="/intelligence-overview" element={<IntelligenceOverview />} />
        <Route path="/solutions" element={<SolutionsIndex />} />

        {/* Solution Modules */}
        <Route path="/solutions/commerce" element={<IBCommerceSolution />} />
        <Route path="/solutions/commerce/overview" element={<IBCommerceOverview />} />
        <Route path="/solutions/commerce/parties" element={<PartiesModule />} />
        <Route path="/solutions/commerce/parties/customers" element={<CustomersPage />} />
        <Route path="/solutions/commerce/parties/vendors" element={<VendorsPage />} />
        <Route path="/solutions/commerce/parties/partners" element={<PartnersPage />} />
        <Route path="/solutions/commerce/parties/channels" element={<ChannelsPage />} />
        <Route path="/solutions/commerce/parties/profiles" element={<ProfilesPage />} />
        <Route path="/solutions/commerce/catalog" element={<CatalogModule />} />
        <Route path="/solutions/commerce/catalog/items" element={<ItemsPage />} />
        <Route path="/solutions/commerce/catalog/pricing" element={<PricingPage />} />
        <Route path="/solutions/commerce/catalog/costing" element={<CostingPage />} />
        <Route path="/solutions/commerce/catalog/packages" element={<PackagesPage />} />
        <Route path="/solutions/commerce/catalog/rules" element={<RulesPage />} />
        <Route path="/solutions/commerce/revenue" element={<RevenueModule />} />
        <Route path="/solutions/commerce/governance" element={<GovernanceModule />} />
        <Route path="/solutions/commerce/governance/policies" element={<PoliciesPage />} />
        <Route path="/solutions/commerce/governance/authority" element={<AuthorityPage />} />
        <Route path="/solutions/commerce/governance/limits" element={<LimitsPage />} />
        <Route path="/solutions/commerce/governance/risk" element={<RiskPage />} />
        <Route path="/solutions/commerce/governance/audit" element={<AuditPage />} />

        <Route path="/solutions/workforce" element={<Navigate to="/solutions/workforce/overview" replace />} />
        <Route path="/solutions/workforce/overview" element={<IBWorkforceOverview />} />
        <Route path="/solutions/workforce/employees" element={<IBWorkforceEmployees />} />
        <Route path="/solutions/workforce/payroll" element={<IBWorkforcePayroll />} />
        <Route path="/solutions/workforce/benefits" element={<IBWorkforceBenefits />} />
        <Route path="/solutions/workforce/performance" element={<IBWorkforcePerformance />} />
        <Route path="/solutions/workforce/learning" element={<IBWorkforceLearning />} />

        <Route path="/solutions/capital" element={<Navigate to="/solutions/capital/overview" replace />} />
        <Route path="/solutions/capital/overview" element={<IBCapitalOverview />} />
        <Route path="/solutions/capital-real" element={<IBCapitalPage />} />
        <Route path="/solutions/capital/banks" element={<IBCapitalBanks />} />
        <Route path="/solutions/capital/treasury" element={<IBCapitalTreasury />} />
        <Route path="/solutions/capital/credit" element={<IBCapitalCredit />} />
        <Route path="/solutions/capital/forecasting" element={<IBCapitalForecasting />} />
        <Route path="/solutions/capital/optimization" element={<IBCapitalOptimization />} />

        <Route path="/solutions/operations" element={<Navigate to="/solutions/operations/overview" replace />} />
        <Route path="/solutions/operations/overview" element={<IBOperationsOverview />} />
        <Route path="/solutions/operations/projects" element={<IBOperationsProjects />} />
        <Route path="/solutions/operations/assets" element={<IBOperationsAssets />} />
        <Route path="/solutions/operations/inventory" element={<IBOperationsInventory />} />
        <Route path="/solutions/operations/production" element={<IBOperationsProduction />} />

        <Route path="/solutions/finance" element={<Navigate to="/solutions/finance/overview" replace />} />
        <Route path="/solutions/finance/overview" element={<IBFinanceOverview />} />
        <Route path="/solutions/finance/accounting" element={<IBFinanceAccounting />} />
        <Route path="/solutions/finance/reporting" element={<IBFinanceReporting />} />
        <Route path="/solutions/finance/budgeting" element={<IBFinanceBudgeting />} />
        <Route path="/solutions/finance/reconciliation" element={<IBFinanceReconciliation />} />
        <Route path="/solutions/finance/compliance" element={<IBFinanceCompliance />} />

        <Route path="/insights" element={<InsightsIndex />} />
        <Route path="/intelligence" element={<IntelligencePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/contract/sign/:token" element={<ContractPublicView />} />
        <Route path="/proposal/respond/:proposalId" element={<ProposalResponse />} />
        <Route path="/client-portal/contracts" element={<ContractPortal />} />
        
        {/* Public Recruitment & Careers Routes */}
        <Route path="/:orgSlug/careers" element={<PublicCareersPage />} />
        <Route path="/:orgSlug/careers/:id" element={<PublicJobDetailPage />} />
        <Route path="/:orgSlug/jobs/:id" element={<PublicJobDetailPage />} />
        <Route path="/careers" element={<PublicCareersPage />} />
        <Route path="/careers/:id" element={<PublicJobDetailPage />} />
        <Route path="/jobs/:id" element={<PublicJobDetailPage />} />
        <Route path="/offer/review/:token" element={<CandidateOfferReviewPage />} />
        <Route path="/public/onboarding" element={<PublicCandidateOnboardingPage />} />

        {/* Legal Policy Routes */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
    </Routes>
);

export default PublicRoutes;
